<template>
    <div>
        <div :class="contentClass" v-if="isCheckboxType">
            <v-radio
                class="stack-s"
                label="In these values"
                :value="false"
                :group="`${stateGroupName}/toggle`"
                :model="isNegativeCheck"
                @update="toggleNegativeCheck"
            />
            <v-radio
                label="Not in these values"
                :value="true"
                :group="`${stateGroupName}/toggle`"
                :model="isNegativeCheck"
                @update="toggleNegativeCheck"
            />
        </div>
        <div :class="contentClass">
            <div v-if="isCheckboxType">
                <p class="stack-m">Select one or more options</p>
                <v-select
                    v-if="requiresDropdown"
                    v-model="value"
                    :options="options"
                    allow-empty
                    multiple/>
                <template v-else>
                    <v-checkbox
                        v-for="option in options"
                        class="stack-s"
                        :key="option.value"
                        :title="option.value"
                        :label="option.label"
                        v-model="value"
                        :value="option.value"/>
                </template>
            </div>
            <div v-else>
                <p class="stack-m">Select an option</p>
                <v-radio
                    v-for="option in options"
                    class="stack-s"
                    :title="option.value"
                    :group="stateGroupName"
                    :key="option.value"
                    :label="option.label"
                    :model="value"
                    @update="v => value = v"
                    :value="option.value"/>
            </div>
        </div>
        <filter-footer
            @apply-filter="applyFilter"
            @cancel-filter="cancelFilter"/>
    </div>
</template>

<script>
    import {vRadio, vCheckbox, vSelect} from '@/components/form';
    import FilterFooter from '@/modules/rules-engine/components/condition-builder/filters/filter-footer';
    import BaseFilterMixin from '@/modules/rules-engine/components/condition-builder/filters/base-filter-mixin';
    import CriteriaCondition from '@/components/query/spec/criteria-condition';
    import API_SPEC_FIELD_TYPES from '@/components/query/helpers/api-spec-field-types';
    import ConditionOperators from '@/components/query/spec/condition-operators';
    import _ from '@/common/helpers';

    export default {
        name: 'enum-filter',
        mixins: [BaseFilterMixin],
        components: {
            FilterFooter,
            vCheckbox,
            vRadio,
            vSelect,
        },
        computed: {
            stateGroupName() {
                return `${this.condition.getPath()}/state`;
            },
            isCheckboxType() {
                return this.getIsCheckboxType();
            },
            options() {
                const toLabel = value => ({value, label: _.unKebabCase(value)});
                if (this.field.definition.enum) {
                    return [...this.field.definition.enum].sort().map(toLabel);
                }
                return [...this.field.definition.additionalSchema.allowedValues].sort().map(toLabel);
            },
            requiresDropdown() {
                return this.options.length > 6;
            },
        },
        data() {
            return {
                value: this.getInitialState(),
                isNegativeCheck: (this.condition.isNegative === true)
                    || (this.condition.isNegativeList === true),
            };
        },
        methods: {
            getIsCheckboxType() {
                // use computed or trigger manually
                const field = this.field || this.getField();
                return field.definition.enum !== undefined
                    || field.definition.type === API_SPEC_FIELD_TYPES.array;
            },
            getInitialState() {
                const isCheckbox = this.getIsCheckboxType();
                if (this.condition.data.isEmpty()) {
                    return isCheckbox ? [] : null;
                }
                if (this.condition.isContains) {
                    return this.condition.listData.values || [];
                }
                if (isCheckbox) {
                    return this.condition.data.values || [];
                }
                return this.condition.data.values
                    // previous rules used `values` ~ BC fix
                    ? this.condition.data.values[0]
                    // updated version uses `value`
                    : this.condition.data.value;
            },
            toggleNegativeCheck(state) {
                this.isNegativeCheck = state;
            },
            applyFilter() {
                this.condition.isNegative = this.isNegativeCheck;
                const payload = {
                    index: this.index,
                    condition: this.condition,
                };
                // custom fields of type array requires a different data object
                if (this.condition.isContains) {
                    payload.condition.listData = payload.condition.listData || {};
                    payload.condition.listData.values = this.value;
                    payload.condition.isNegativeList = this.condition.isNegative;
                } else if (this.isCheckboxType) {
                    if (!this.value.length) {
                        return;
                    }
                    payload.condition.data = new CriteriaCondition({
                        op: ConditionOperators.in,
                        path: this.condition.data.getPath(),
                        values: this.value,
                    });
                } else {
                    if (this.value === null) {
                        return;
                    }
                    payload.condition.data = new CriteriaCondition({
                        op: ConditionOperators.equals,
                        path: this.condition.data.getPath(),
                        value: this.value,
                    });
                }
                this.$emit('apply-filter', payload);
            },
        },
    };
</script>
